import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtCompact } from "@/lib/format";
import { DEFAULT_STAGES } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, Skeleton, Segmented, SearchField } from "@/components/common";
import { OppCard } from "@/components/pipeline/OppCard";
import { StageColumn, StageEmpty } from "@/components/pipeline/StageColumn";
import { LostDialog } from "@/components/opportunity/LostDialog";
import { useAuth } from "@/context/AuthContext";

function NewOpportunityDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [account, setAccount] = useState("");
  const [value, setValue] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/opportunities", { name, account_name: account, value: Number(value || 0), expected_close_date: date || null });
      toast.success("Opportunity created");
      onOpenChange(false);
      setName(""); setAccount(""); setValue(""); setDate("");
      onCreated(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-opportunity-dialog">
        <DialogHeader><DialogTitle>New opportunity</DialogTitle><DialogDescription>Only the essentials. Everything else is captured as you work.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Customer</Label><Input value={account} onChange={(e) => setAccount(e.target.value)} placeholder="e.g. NRK" required data-testid="new-opportunity-account-input" /></div>
          <div className="space-y-1.5"><Label>Opportunity name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Studio Bergen" required data-testid="new-opportunity-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Estimated value (NOK)</Label><Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="0" data-testid="new-opportunity-value-input" /></div>
            <div className="space-y-1.5"><Label>Expected decision</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="new-opportunity-date-input" /></div>
          </div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="new-opportunity-submit-button">{busy ? "Creating..." : "Create"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PipelinePage() {
  const [opps, setOpps] = useState(null);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [users, setUsers] = useState([]);
  const [owner, setOwner] = useState("all");
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [lostFor, setLostFor] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openInquiry } = useOutletContext();
  const [params] = useSearchParams();
  const focusStage = params.get("stage");
  const boardRef = useRef(null);

  const load = () => api.get("/opportunities", { params: { status: "all" } }).then((r) => setOpps(r.data));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => r.data?.stages?.length && setStages(r.data.stages)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!focusStage || !opps || !boardRef.current) return;
    const el = boardRef.current.querySelector(`[data-testid="pipeline-stage-column-${focusStage}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [focusStage, opps]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (opps || []).filter((o) => (owner === "all" || (owner === "mine" ? o.owner_id === user?.id : o.owner_id === owner)) && (type === "all" || o.type === type) && (!s || `${o.account_name} ${o.name}`.toLowerCase().includes(s)));
  }, [opps, owner, type, q, user]);
  const visibleStages = stages.filter((s) => showClosed || !["won", "lost"].includes(s.key));
  const prob = useMemo(() => Object.fromEntries(stages.map((s) => [s.key, s.probability || 0])), [stages]);

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    const newStage = destination.droppableId;
    const opp = opps.find((o) => o.id === draggableId);
    if (!opp) return;
    if (newStage === "lost") {
      setLostFor(opp);
      return;
    }
    if (newStage === "won") {
      try {
        await api.post(`/opportunities/${opp.id}/won`);
        toast.success(`${opp.account_name} / ${opp.name} marked as Won`);
        navigate(`/opportunities/${opp.id}?handover=1`);
      } catch (e) {
        toast.error(errMsg(e));
      }
      return;
    }
    const prev = opps;
    setOpps(opps.map((o) => (o.id === draggableId ? { ...o, stage: newStage, status: "open" } : o)));
    try {
      await api.patch(`/opportunities/${draggableId}`, { stage: newStage });
      toast.success(`Moved to ${stages.find((s) => s.key === newStage)?.label || newStage}`);
      load();
    } catch (e) {
      setOpps(prev);
      toast.error(errMsg(e));
    }
  };

  const open = filtered.filter((o) => o.status === "open");
  const totalOpen = open.reduce((s, o) => s + (o.value || 0), 0);
  const weighted = open.reduce((s, o) => s + (o.value || 0) * (prob[o.stage] || 0) / 100, 0);
  const ownerItems = [{ key: "all", label: "Everyone" }, { key: "mine", label: "Mine" }, ...users.filter((u) => u.id !== user?.id).map((u) => ({ key: u.id, label: (u.name || "").split(" ")[0] }))];

  return (
    <div className="flex h-full flex-col">
      <div className="px-5 pt-8 sm:px-8 lg:px-10">
        <PageHeader
          eyebrow="Sales"
          title="Opportunities"
          className="mb-5"
          description={opps ? (
            <span className="inline-flex flex-wrap items-center gap-x-2">
              <span><span className="font-semibold text-foreground tabular">{open.length}</span> open</span>
              <span className="text-border">·</span>
              <span><span className="font-semibold text-foreground tabular">{fmtCompact(totalOpen)}</span> in pipeline</span>
              <span className="text-border">·</span>
              <span><span className="font-semibold text-foreground tabular">{fmtCompact(weighted)}</span> weighted</span>
            </span>
          ) : "Loading pipeline"}
          actions={
            <>
              <Button variant="outline" className="gap-2" onClick={() => setNewOpen(true)} data-testid="pipeline-new-opportunity-button"><Plus className="h-4 w-4" /> Opportunity</Button>
              <Button className="gap-2" onClick={openInquiry} data-testid="pipeline-new-inquiry-button"><Plus className="h-4 w-4" /> New inquiry</Button>
            </>
          }
        />
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <div data-testid="pipeline-owner-filter">
              <Segmented items={ownerItems} value={owner} onChange={setOwner} testIdPrefix="pipeline-owner" />
            </div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-10 w-[130px] rounded-xl border-black/[0.09] bg-white text-[13px]" data-testid="pipeline-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem><SelectItem value="standard">Standard</SelectItem><SelectItem value="tender">Tenders</SelectItem></SelectContent>
            </Select>
            <Button variant={showClosed ? "soft" : "ghost"} className="h-10 rounded-xl text-[13px]" onClick={() => setShowClosed((v) => !v)} data-testid="pipeline-toggle-closed-button">{showClosed ? "Hide" : "Show"} Won / Lost</Button>
          </div>
          <SearchField value={q} onChange={setQ} placeholder="Filter by customer or name" className="w-full lg:w-[260px]" testId="pipeline-search-input" />
        </div>
      </div>

      <div ref={boardRef} className="min-h-0 flex-1 overflow-x-auto px-5 pb-8 scrollbar-thin sm:px-8 lg:px-10">
        {!opps ? (
          <div className="flex gap-4">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-80 w-[292px] shrink-0 rounded-2xl" />)}</div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="flex h-full items-start gap-3.5" data-testid="pipeline-board">
              {visibleStages.map((s) => {
                const items = filtered.filter((o) => o.stage === s.key);
                const sum = items.reduce((a, o) => a + (o.value || 0), 0);
                const focused = focusStage === s.key;
                return (
                  <Droppable droppableId={s.key} key={s.key}>
                    {(prov, snap) => (
                      <StageColumn ref={prov.innerRef} {...prov.droppableProps} stage={s} count={items.length} sum={sum} focused={focused} draggingOver={snap.isDraggingOver}>
                        {items.map((o, i) => <OppCard key={o.id} o={o} index={i} onOpen={(x) => navigate(`/opportunities/${x.id}`)} />)}
                        {prov.placeholder}
                        {items.length === 0 && !snap.isDraggingOver && <StageEmpty label={s.label} />}
                      </StageColumn>
                    )}
                  </Droppable>
                );
              })}
            </div>
          </DragDropContext>
        )}
      </div>
      <NewOpportunityDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(o) => navigate(`/opportunities/${o.id}`)} />
      {lostFor && <LostDialog opportunity={lostFor} open onOpenChange={(v) => !v && setLostFor(null)} onDone={() => { setLostFor(null); load(); }} />}
    </div>
  );
}
